// The import flow: confirm the target model -> review the column mapping -> preview,
// edit and upload.
//
// Takes already-parsed sheet rows so it stays independent of how the file was read
// (single sheet, multi-tab workbook, or a future CSV path).
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Paper,
    Step,
    StepLabel,
    Stepper,
    Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { PositionModel } from '../../../../Generated/Commodity-Management-Api';
import { findDuplicatePosition } from '../../../../Helpers';
import BulkEditToolbar, { BulkEditScope } from './BulkEditToolbar';
import ColumnMappingTable from './ColumnMappingTable';
import { FieldMappings, buildFieldMappings, getColumnUnion, toPersistableMappings } from './ColumnMapping';
import { detectImportModel } from './DetectImportModel';
import ImportReviewGrid, { ImportReviewGridRef } from './ImportReviewGrid';
import {
    ImportContext,
    ImportRow,
    ValidatedImportRow,
    buildImportRows,
    validateImportRow,
} from './ImportRow';
import { parseMonthValue } from './ImportValueParsers';
import ModelPicker from './ModelPicker';
import {
    ImportModelDescriptor,
    getActiveFields,
    getImportModel,
    getMappableFields,
} from './ImportModelRegistry';
import { ImportResult, getImportSubmitter } from './ImportSubmitters';

const STEPS = ['What is this data?', 'Check the columns', 'Review and upload'];

export interface ImportWizardProps {
    fileName: string;
    /** Row objects keyed by column header, as produced by XLSX.utils.sheet_to_json. */
    rawRows: Record<string, unknown>[];
    sheetNames?: string[];
    activeSheetName?: string;
    ctx: ImportContext;
    /**
     * Model to start on, when the caller has already established what the file holds -
     * the uploaded-files review screen classifies a file (and asks the user when it
     * cannot tell) before it ever reaches the wizard, and asking the same question twice
     * would be indefensible. Omitted, the wizard detects as before.
     */
    initialModelKey?: string;
    /** Previously confirmed mappings for this file shape, if any. */
    savedMappings?: Record<string, string | null>;
    onPersistMappings?: (modelKey: string, mappings: Record<string, string>) => void;
    onComplete: (result: ImportResult, model: ImportModelDescriptor) => void;
    onCancel: () => void;
}

export default function ImportWizard(props: ImportWizardProps) {
    const {
        fileName, rawRows, sheetNames, activeSheetName, ctx, initialModelKey,
        savedMappings, onPersistMappings, onComplete, onCancel,
    } = props;

    const columns = useMemo(() => getColumnUnion(rawRows), [rawRows]);

    const detection = useMemo(
        () => detectImportModel(columns, { fileName, sheetNames }),
        [columns, fileName, sheetNames]
    );

    const startingModelKey = initialModelKey ?? detection.confident?.model.key ?? null;

    const [modelKey, setModelKey] = useState<string | null>(startingModelKey);
    const [step, setStep] = useState(startingModelKey ? 1 : 0);
    // Field key -> chosen file column. An explicit null means the user said the file has
    // no column for that field, which must survive re-derivation of the mappings.
    const [userOverrides, setUserOverrides] = useState<Record<string, string | null>>({});
    const [editedValues, setEditedValues] = useState<Record<string, Record<string, unknown>>>({});
    const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
    const [existingPositions, setExistingPositions] = useState<PositionModel[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [showDuplicates, setShowDuplicates] = useState(false);
    const gridRef = useRef<ImportReviewGridRef>(null);

    const model = modelKey ? getImportModel(modelKey) : undefined;

    const mappableFields = useMemo(
        () => (model ? getMappableFields(model, ctx.customerSettings) : []),
        [model, ctx.customerSettings]
    );
    const activeFields = useMemo(
        () => (model ? getActiveFields(model, ctx.customerSettings) : []),
        [model, ctx.customerSettings]
    );

    const sampleValues = useMemo(() => {
        const samples: Record<string, unknown[]> = {};
        for (const column of columns) {
            samples[column] = rawRows
                .slice(0, 20)
                .map(row => row[column])
                .filter(v => v !== undefined && v !== null && v !== '')
                .slice(0, 3);
        }
        return samples;
    }, [columns, rawRows]);

    // Samples are fed in so a column of full dates can be recognised - that is what lets
    // one date column fill both the month and the year of a date group.
    const mappings: FieldMappings = useMemo(() => {
        if (!model) return {};
        return buildFieldMappings({
            columns,
            fields: mappableFields,
            savedMappings,
            userOverrides,
            sampleValues,
        });
    }, [model, columns, mappableFields, savedMappings, userOverrides, sampleValues]);

    // Base rows from the file, before any in-grid edits.
    const parsedRows = useMemo<ImportRow[]>(() => {
        if (!model) return [];
        return buildImportRows(rawRows, mappings, model, ctx, { sheetName: activeSheetName });
    }, [model, rawRows, mappings, ctx, activeSheetName]);

    // Rows with the user's grid edits layered on top.
    const rows = useMemo<ImportRow[]>(
        () => parsedRows.map(row => {
            const edits = editedValues[row.id];
            return edits ? { ...row, values: { ...row.values, ...edits } } : row;
        }),
        [parsedRows, editedValues]
    );

    // Live validation over the edited values - drives the Status column, cell
    // highlighting and what actually gets uploaded.
    const validations = useMemo(() => {
        const map = new Map<string, ValidatedImportRow>();
        if (!model) return map;
        for (const row of rows) {
            map.set(row.id, validateImportRow(row, model, ctx));
        }
        return map;
    }, [rows, model, ctx]);

    // The editable/duplicate split is computed from the FILE's values, not the edited
    // ones, so these arrays keep a stable identity while the user types. The grid uses
    // them as its data source and Handsontable reloads (losing selection and scroll) if
    // that identity changes - see the ownership note in ImportReviewGrid.
    const duplicateRowIds = useMemo(() => {
        const ids = new Set<string>();
        if (!model || model.key !== 'positions' || existingPositions.length === 0) return ids;

        for (const row of parsedRows) {
            const validated = validateImportRow(row, model, ctx);
            const duplicate = findDuplicatePosition(
                {
                    tradeDate: validated.resolved.tradeDate as string | undefined,
                    volume: validated.resolved.volume as number,
                    price: validated.resolved.price as number,
                    unit: validated.resolved.unit as any,
                    side: validated.resolved.side as any,
                    commodityId: (validated.resolved.commodity as any)?.commodityId,
                    counterpartyId: (validated.resolved.counterparty as any)?.counterpartyId,
                    budgetDate: buildContractDate(row.values.budgetMonth, validated.resolved.budgetYear),
                    contractDate: buildContractDate(row.values.contractMonth, validated.resolved.contractYear),
                },
                existingPositions
            );
            if (duplicate) ids.add(row.id);
        }
        return ids;
    }, [parsedRows, model, ctx, existingPositions]);

    const editableRows = useMemo(
        () => parsedRows.filter(r => !duplicateRowIds.has(r.id)),
        [parsedRows, duplicateRowIds]
    );
    const duplicateRows = useMemo(
        () => parsedRows.filter(r => duplicateRowIds.has(r.id)),
        [parsedRows, duplicateRowIds]
    );

    const errorCount = useMemo(
        () => editableRows.filter(r => (validations.get(r.id)?.errors.length ?? 0) > 0).length,
        [editableRows, validations]
    );
    const warningCount = useMemo(
        () => editableRows.filter(r => (validations.get(r.id)?.warnings.length ?? 0) > 0).length,
        [editableRows, validations]
    );
    const validCount = editableRows.length - errorCount;

    // Fetch existing positions once the model and rows are known, so duplicates can be
    // flagged. Deliberately driven off parsedRows (the file), not rows (which change on
    // every cell edit) - re-fetching per keystroke was a known freeze.
    useEffect(() => {
        if (model?.key !== 'positions' || parsedRows.length === 0) {
            setExistingPositions([]);
            return;
        }

        let cancelled = false;
        const tradeDates = parsedRows
            .map(r => r.values.tradeDate)
            .filter((d): d is string => typeof d === 'string' && d.length > 0)
            .sort();
        if (tradeDates.length === 0) return;

        // Positional argument list copied from ChatAssistant.loadExistingPositionsForRows -
        // listPositions takes ~30 params and the order is not guessable.
        CommodityManagementApi.listPositions(
            null,
            undefined, // quoteDate
            false,     // includeExpirationDates
            false,     // includeMarketPrices
            false,     // useDisplayUnits
            undefined, // scenarioMarketPrices
            undefined, // commoditiesFilter
            undefined, // locationsFilter
            undefined, // counterpartiesFilter
            tradeDates[0],                        // tradeDateStartFilter
            tradeDates[tradeDates.length - 1],    // tradeDateEndFilter
            undefined, undefined, undefined,   // budgetMonth start year/month/label
            undefined, undefined, undefined,   // budgetMonth end year/month/label
            undefined, undefined, undefined,   // contractMonth start year/month/label
            undefined, undefined, undefined,   // contractMonth end year/month/label
            undefined, // positionTypesFilter
            undefined, // dealTicketNumberFilter
            undefined, // dealTicketIdFilter
            undefined, // sideFilter
            undefined, // validatedFilter
            undefined, // currencyFilter
            undefined, // currency
            undefined, // expiryStatus
            false,     // includeAllocations
            false,     // includeAllocationOffset
            true,      // filterByTradeDate
        )
            .then(response => {
                if (!cancelled) setExistingPositions(response.data.rows ?? []);
            })
            .catch(error => {
                console.error('Failed to load existing positions for duplicate check', error);
                if (!cancelled) setExistingPositions([]);
            });

        return () => { cancelled = true; };
    }, [model?.key, parsedRows]);

    // Remapping a column re-derives every row from the file, so edits keyed to the old
    // interpretation no longer apply. Clearing them avoids stale values silently
    // surviving a mapping change.
    useEffect(() => {
        setEditedValues({});
        setSelectedRowIds([]);
    }, [parsedRows]);

    const handleChangeCell = useCallback((rowId: string, fieldKey: string, value: unknown) => {
        setEditedValues(prev => ({
            ...prev,
            [rowId]: { ...(prev[rowId] ?? {}), [fieldKey]: value },
        }));
    }, []);

    const handleBulkApply = useCallback((fieldKey: string, value: unknown, scope: BulkEditScope) => {
        const targetIds = scope === 'selected'
            ? selectedRowIds
            : editableRows
                .filter(row => scope === 'all' || isBlank(row.values[fieldKey]))
                .map(row => row.id);

        if (targetIds.length === 0) return;

        // Write through the grid rather than into React state: Handsontable owns the
        // data once loaded, and its afterChange handler feeds the new values back into
        // editedValues so validation stays in step.
        gridRef.current?.applyBulkEdit(targetIds, fieldKey, value);
    }, [selectedRowIds, editableRows]);

    async function handleUpload() {
        if (!model) return;
        const submitter = getImportSubmitter(model.key);
        if (!submitter) return;

        const uploadable = editableRows
            .map(row => validations.get(row.id))
            .filter((v): v is ValidatedImportRow => !!v && v.errors.length === 0);

        if (uploadable.length === 0) return;

        setIsUploading(true);
        try {
            const result = await submitter(uploadable, ctx);
            onPersistMappings?.(model.key, toPersistableMappings(mappings));
            onComplete(result, model);
        } finally {
            setIsUploading(false);
        }
    }

    if (columns.length === 0) {
        return <Alert severity="warning">That file has no readable columns.</Alert>;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, minHeight: 0 }}>
            <Stepper activeStep={step} sx={{ flexShrink: 0 }}>
                {STEPS.map((label, index) => (
                    <Step key={label} completed={step > index}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {step === 0 && (
                <ModelPicker
                    fileName={fileName}
                    detection={detection}
                    onSelect={key => { setModelKey(key); setStep(1); }}
                />
            )}

            {step === 1 && model && (
                <>
                    <Alert
                        severity="info"
                        variant="outlined"
                        action={
                            <Button size="small" onClick={() => setStep(0)}>
                                Not {model.plural}?
                            </Button>
                        }
                    >
                        Importing <strong>{rawRows.length}</strong> rows as{' '}
                        <strong>{model.plural}</strong>.
                    </Alert>

                    <ColumnMappingTable
                        columns={columns}
                        sampleValues={sampleValues}
                        fields={mappableFields}
                        mappings={mappings}
                        onChangeMapping={(fieldKey, column) =>
                            setUserOverrides(prev => ({ ...prev, [fieldKey]: column }))
                        }
                    />

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button onClick={onCancel}>Cancel</Button>
                        <Button onClick={() => setStep(2)}>
                            Preview data
                        </Button>
                    </Box>
                </>
            )}

            {step === 2 && model && (
                <>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip icon={<CheckCircleIcon />} color="success" size="small"
                              label={`${validCount} ready`} />
                        {errorCount > 0 && (
                            <Chip icon={<ErrorIcon />} color="error" size="small"
                                  label={`${errorCount} with errors`} />
                        )}
                        {warningCount > 0 && (
                            <Chip icon={<WarningAmberIcon />} color="warning" size="small"
                                  variant="outlined" label={`${warningCount} with warnings`} />
                        )}
                        {duplicateRows.length > 0 && (
                            <Chip size="small" variant="outlined"
                                  label={`${duplicateRows.length} already exist - will be skipped`} />
                        )}
                    </Box>

                    <BulkEditToolbar
                        fields={activeFields}
                        ctx={ctx}
                        selectedCount={selectedRowIds.length}
                        onApply={handleBulkApply}
                    />

                    <ImportReviewGrid
                        ref={gridRef}
                        fields={activeFields}
                        rows={editableRows}
                        validations={validations}
                        ctx={ctx}
                        onChangeCell={handleChangeCell}
                        onSelectionChange={setSelectedRowIds}
                    />

                    {duplicateRows.length > 0 && (
                        <Paper variant="outlined">
                            <Button
                                fullWidth
                                onClick={() => setShowDuplicates(v => !v)}
                                endIcon={showDuplicates ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                sx={{ justifyContent: 'space-between', textTransform: 'none' }}
                            >
                                <Typography variant="body2">
                                    {duplicateRows.length} row
                                    {duplicateRows.length === 1 ? '' : 's'} already in the system
                                </Typography>
                            </Button>
                            <Collapse in={showDuplicates}>
                                <Box sx={{ p: 1 }}>
                                    <ImportReviewGrid
                                        fields={activeFields}
                                        rows={duplicateRows}
                                        validations={validations}
                                        ctx={ctx}
                                        onChangeCell={handleChangeCell}
                                        readOnly
                                        height={240}
                                    />
                                </Box>
                            </Collapse>
                        </Paper>
                    )}

                    {errorCount > 0 && (
                        <Alert severity="warning" variant="outlined">
                            {errorCount} row{errorCount === 1 ? '' : 's'} still have errors and will
                            not be uploaded. Fix them in the grid, or upload the {validCount} valid
                            row{validCount === 1 ? '' : 's'} now.
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setStep(1)}>Back to columns</Button>
                        <Button onClick={onCancel}>Cancel</Button>
                        <Button
                            disabled={validCount === 0 || isUploading}
                            onClick={handleUpload}
                            startIcon={isUploading ? <CircularProgress size={16} /> : undefined}
                        >
                            {isUploading
                                ? 'Uploading...'
                                : `Upload ${validCount} ${validCount === 1 ? model.singular : model.plural}`}
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
}

function isBlank(value: unknown): boolean {
    return value === undefined || value === null || value === '';
}

function buildContractDate(month: unknown, year: unknown) {
    // Via the shared parsers so a month cell holding a full date (which is what a date
    // column mapped to both halves of a date group puts here) still resolves.
    const m = parseMonthValue(month);
    const y = typeof year === 'number' ? year : Number(year);
    if (!m || !y || isNaN(y)) return undefined;
    return { month: m, year: y };
}
